import { getManageFlowBookingById } from '@/features/manage/manageActions';
import { startExchange } from '@/features/purchase/purchaseActions';
import {
  inboundDataSelector,
  outboundDataSelector,
} from '@/features/purchase/purchaseSelectors';
import { useBookingCreation } from '@/hooks/useBookingCreation';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useGTMSearchContext } from '@/hooks/useGTMSearchContext';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import {
  ALERT_REASON,
  getAlertReasonFromApiError,
  type AlertReason,
} from '@/utils/changeTrips';
import {
  GTM_EVENT,
  trackCustomEvent,
  trackEcommerceEventV2,
} from '@/utils/gtm';
import { mapJourneyToGTMItem, roundMoney } from '@/utils/gtmPayloads';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseJourneyOfferContinueProps {
  isOutbound: boolean;
  isChangeTravelClassFlow: boolean;
  hasInboundJourneys?: boolean;
  getChangeTravelClassOffers: () => Promise<true | AlertReason | null>;
  showStartExchangeError: (err?: unknown) => void;
}

export const useJourneyOfferContinue = ({
  isOutbound,
  isChangeTravelClassFlow,
  hasInboundJourneys,
  getChangeTravelClassOffers,
  showStartExchangeError,
}: UseJourneyOfferContinueProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [changeTravelClassAlert, setChangeTravelClassAlert] =
    useState<AlertReason | null>(null);
  const {
    hasSelection,
    booking: manageBooking,
    selectedInboundTrip,
  } = useChangeBookingSelection();
  const { handleCreatingAndFetchingTripOfferBooking } = useBookingCreation();
  const { selectedJourney, selectedOfferMapByLegId } = useSelector(
    isOutbound ? outboundDataSelector : inboundDataSelector
  );
  const { totalPassengers, roundTrip } = useGTMSearchContext();

  const handleAlertBack = useCallback(() => {
    navigate('/manage-booking/details', {
      state: { reopenChangeTripsModal: true },
    });
  }, [navigate]);

  const handleAlertContinue = useCallback(() => {
    setChangeTravelClassAlert(null);
  }, []);

  const onContinue = useCallback(async () => {
    trackCustomEvent(
      isOutbound
        ? GTM_EVENT.OUTBOUND_OFFER_CONFIRMED
        : GTM_EVENT.INBOUND_OFFER_CONFIRMED
    );

    // Tagging plan (BR-57983): the same offer can cover several legs, so
    // dedupe by offer id before summing the direction's selected offers.
    if (selectedJourney) {
      const uniqueOffers = [
        ...new Map(
          Object.values(selectedOfferMapByLegId).map((offer) => [
            offer.id,
            offer,
          ])
        ).values(),
      ];
      const offersTotal = roundMoney(
        uniqueOffers.reduce((sum, offer) => sum + (offer.price?.amount ?? 0), 0)
      );

      trackEcommerceEventV2(GTM_EVENT.ADD_TO_BOOKING, {
        currency:
          uniqueOffers[0]?.price?.currency ??
          selectedJourney.lowestPrice?.currency,
        value: offersTotal || undefined,
        total_passenger: totalPassengers || undefined,
        round_trip: roundTrip,
        items: [
          mapJourneyToGTMItem(
            selectedJourney,
            totalPassengers || 1,
            offersTotal || undefined
          ),
        ],
      });
    }

    if (isOutbound) {
      if (isChangeTravelClassFlow && selectedInboundTrip?.id) {
        return navigate('/purchase/inbound/offer');
      }
      if (hasInboundJourneys) {
        return navigate('/purchase/inbound/journey');
      }
    }

    if (hasSelection) {
      // Change-booking commit: create the exchange operation, refresh the
      // manage booking so it carries the new exchangeOperations[], then
      // enter checkout.
      try {
        await dispatch(startExchange()).unwrap();
        if (manageBooking?.id) {
          await dispatch(getManageFlowBookingById(manageBooking.id)).unwrap();
        }
        navigate('/purchase/checkout');
      } catch (err) {
        const errors = (err as ApiErrorResponse | undefined)?.response?.data
          ?.errors;
        if (isChangeTravelClassFlow) {
          const alertReason = getAlertReasonFromApiError(errors?.[0]);
          if (alertReason) {
            setChangeTravelClassAlert(alertReason);
            return;
          }
          // https://youtrack.turnit.tech/issue/BR-56905/WS-2.0-My-account-Tickets-Change-travel-class-2-3#focus=Comments-58-238055.0-0
          const code = errors?.[0].code;
          if (code === 'INTERNAL_ERROR' || code === '') {
            const result = await getChangeTravelClassOffers();
            if (result === true) {
              setChangeTravelClassAlert({
                code: ALERT_REASON.SELECTED_TICKET_JUST_SOLD_OUT,
                message: '',
              });
            } else {
              setChangeTravelClassAlert(result);
            }
            return;
          }
        }
        showStartExchangeError(err);
      }
      return;
    }

    handleCreatingAndFetchingTripOfferBooking();
  }, [
    dispatch,
    getChangeTravelClassOffers,
    handleCreatingAndFetchingTripOfferBooking,
    hasInboundJourneys,
    hasSelection,
    isChangeTravelClassFlow,
    isOutbound,
    manageBooking?.id,
    navigate,
    roundTrip,
    selectedInboundTrip?.id,
    selectedJourney,
    selectedOfferMapByLegId,
    showStartExchangeError,
    totalPassengers,
  ]);

  return {
    onContinue,
    changeTravelClassAlert,
    handleAlertBack,
    handleAlertContinue,
    booking: manageBooking,
  };
};
